import { loadAllForms } from "@/lib/admin/loadFormsList";
import {
  enrichFormsWithAnswersCounts,
  formsNeedAnswersCountEnrichment,
} from "@/lib/admin/enrichFormsListCounts";
import { formatApiQueryError } from "@/lib/http/formatQueryError";
import type { FormListItemDto } from "@/types/formList";
import { useEffect, useRef } from "react";
import { useAsyncResource } from "./useAsyncResource";

interface UseFormsListQueryOptions {
  syncYandex?: boolean;
  organisationId: number | null;
  unassignedOnly?: boolean;
  serviceOnly?: boolean;
}

interface UseFormsListQueryResult {
  forms: FormListItemDto[];
  isLoading: boolean;
  error: string | null;
  reload: () => void;
}

export function useFormsListQuery(
  options: UseFormsListQueryOptions,
): UseFormsListQueryResult {
  const {
    syncYandex = false,
    organisationId,
    unassignedOnly = false,
    serviceOnly = false,
  } = options;
  const syncYandexOnceRef = useRef(syncYandex);

  const { data, isLoading, error, reload, setData } = useAsyncResource<
    FormListItemDto[]
  >({
    enabled: organisationId != null || unassignedOnly || serviceOnly,
    deps: [syncYandex, organisationId, unassignedOnly, serviceOnly],
    fetch: () =>
      loadAllForms({
        syncYandex: syncYandexOnceRef.current,
        organisationId: organisationId ?? undefined,
        unassignedOnly,
        serviceOnly,
        excludeServiceForms: organisationId != null,
      }).finally(() => {
        syncYandexOnceRef.current = false;
      }),
    formatError: (err) =>
      formatApiQueryError(err, "Не удалось загрузить список форм", {
        statusPrefix: "Не удалось загрузить список форм",
      }),
  });

  useEffect(() => {
    if (!data?.length) return;
    if (formsNeedAnswersCountEnrichment(data).length === 0) return;

    let cancelled = false;

    void enrichFormsWithAnswersCounts(data).then((enriched) => {
      if (!cancelled) setData(enriched);
    });

    return () => {
      cancelled = true;
    };
  }, [data, setData]);

  return {
    forms: data ?? [],
    isLoading,
    error,
    reload,
  };
}

export { FORMS_LIST_PER_PAGE_OPTIONS } from "./formsListPerPage";
